import type { AxiosError } from 'axios';

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { api } from '../../api';
import {
  getStoredDocPrivacyShown,
  getStoredDocTermsShown,
  // getStoredSafeExitWizardShown,
  setStoredDocPrivacyShown,
  setStoredDocTermsShown,
} from '../../helpers/device.helper';
import type { ErrorMessage } from '../../models/Error';
import type { AsyncThunkConfig } from '../../models/slice';
import { RaygunErrorHandlerService } from '../../services/raygun';
// import { setWizardModal, WIZARD_MODAL } from '../guest/guestSlice';

const { logError } = RaygunErrorHandlerService();

export const DOC_MODAL = {
  IDLE: 'idle',
  TERMS: 'rules_terms_conditions',
  PRIVACY: 'privacy_policy',
} as const;

export type DOC_MODAL = (typeof DOC_MODAL)[keyof typeof DOC_MODAL];

export type AppDocument = {
  id: string;
  document_type: 'privacy_policy' | 'rules_terms_conditions';
  never_acknowledged: boolean;
  has_updated: boolean | null;
  copy: string;
};

type DocumentsSliceType = {
  docs: Record<string, AppDocument> | null;
  docModal: DOC_MODAL;
  isDocReview: boolean;
};

const initialState: DocumentsSliceType = {
  docModal: DOC_MODAL.IDLE,
  docs: null,
  isDocReview: false,
};

export const fetchDocuments = createAsyncThunk<
  { docModal: DOC_MODAL; docs: Record<string, AppDocument> | null },
  undefined,
  AsyncThunkConfig
>('documents/fetchDocuments', async (_, thunkAPI) => {
  try {
    const response = await api.post<AppDocument[]>('/guest/v1_get_latest_documents', {
      language_code: 'en', // fetch only English until other languages are supported
    });

    if (!response) {
      return { docModal: DOC_MODAL.IDLE, docs: null };
    }

    const docs = Object.fromEntries(response.map((d) => [d.document_type, d]));
    const termsDoc = docs.rules_terms_conditions;
    const privacyDoc = docs.privacy_policy;

    if (!getStoredDocTermsShown() || termsDoc.never_acknowledged || termsDoc.has_updated) {
      return { docModal: DOC_MODAL.TERMS, docs };
    } else if (!getStoredDocPrivacyShown() || privacyDoc.never_acknowledged || privacyDoc.has_updated) {
      return { docModal: DOC_MODAL.PRIVACY, docs };
    } else {
      // if (!getStoredSafeExitWizardShown()) {
      //   setTimeout(() => {
      //     thunkAPI.dispatch(setWizardModal(WIZARD_MODAL.SAFE_EXIT));
      //   }, 800);
      // }
      return { docModal: DOC_MODAL.IDLE, docs };
    }
  } catch (e) {
    logError(e, ['documentsSlice', 'fetchDocuments']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const logDocumentAcknowledgement = createAsyncThunk<boolean | undefined, string | undefined, AsyncThunkConfig>(
  'documents/logDocumentAcknowledgement',
  async (document_id, thunkAPI) => {
    try {
      if (!document_id) return;
      const response = await api.post<boolean>('/guest/v0_log_document_acknowledgement', { document_id });
      const docModal = thunkAPI.getState().documentsSlice.docModal;
      if (docModal === DOC_MODAL.TERMS) setStoredDocTermsShown();
      if (docModal === DOC_MODAL.PRIVACY) setStoredDocPrivacyShown();
      await thunkAPI.dispatch(fetchDocuments());
      return response;
    } catch (e) {
      logError(e, ['documentsSlice', 'logDocumentAcknowledgement']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setDocModal: (state, action: PayloadAction<DOC_MODAL>) => {
      state.isDocReview = action.payload !== DOC_MODAL.IDLE;
      state.docModal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDocuments.fulfilled, (state, action) => {
      state.docs = action.payload.docs;
      state.docModal = action.payload.docModal;
    });
  },
});

export const { setDocModal } = documentsSlice.actions;
